import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private _theme = signal<'light' | 'dark'>('light');
  private _preference = signal<'light' | 'dark' | 'system'>('light');
  private systemQuery?: MediaQueryList;

  constructor() {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      this.systemQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.attachSystemListener();
    }

    const storedPreference = this.readPreference();
    this._preference.set(storedPreference);
    this.applyThemePreference(storedPreference);
  }

  get theme() {
    return this._theme.asReadonly();
  }

  get themePreference() {
    return this._preference.asReadonly();
  }

  setTheme(preference: 'light' | 'dark' | 'system') {
    this._preference.set(preference);
    this.persistPreference(preference);
    this.applyThemePreference(preference);
  }

  private readPreference(): 'light' | 'dark' | 'system' {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return 'light';
    const saved = sessionStorage.getItem('themePreference');
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved;
    }
    return 'light';
  }

  private persistPreference(preference: 'light' | 'dark' | 'system') {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem('themePreference', preference);
  }

  private applyThemePreference(preference: 'light' | 'dark' | 'system') {
    const resolved = preference === 'system' ? this.getSystemTheme() : preference;
    this._theme.set(resolved);
    this.applyGlobalTheme(resolved);
  }

  private getSystemTheme(): 'light' | 'dark' {
    return this.systemQuery?.matches ? 'dark' : 'light';
  }

  private attachSystemListener() {
    if (!this.systemQuery) return;
    const handler = () => {
      if (this._preference() === 'system') {
        this.applyThemePreference('system');
      }
    };

    if ('addEventListener' in this.systemQuery) {
      this.systemQuery.addEventListener('change', handler);
      return;
    }

    const legacyQuery = this.systemQuery as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };
    legacyQuery.addListener?.(handler);
  }

  private applyGlobalTheme(theme: 'light' | 'dark') {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(theme);
      // Mirror onto <html data-theme> as well. The design-system tokens
      // (sass/_tokens.scss) respond to both the body class and this attribute,
      // so this is the forward-compatible switch point as the migration moves
      // off per-component `.dark` overrides toward token-only theming.
      document.documentElement.setAttribute('data-theme', theme);
    }
  }

  toggleTheme() {
    const next = this._theme() === 'light' ? 'dark' : 'light';
    this.setTheme(next);
  }
}
